import { EventFeedEvent } from '@api';
import { InsightsLivePrice } from '@terminal/components/insights-live-price/InsightsLivePrice';
import { config } from '@terminal/config/app.config';
import { useEntities } from '@terminal/react-query-hooks/entities/useEntities';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { getCardDate } from '@terminal/utils/card-utils/card-utils';
import { CardPill } from '@terminal/views/insight-feed/components/insight-feed-card/components/card-pill/CardPill';
import { useCatalystDrawer } from '@terminal/widgets/catalyst-insights-drawer/useCatalystDrawer';
import { useMarketCatalystDrawer } from '@terminal/widgets/market-catalyst-drawer/useMarketCatalystDrawer';
import clsx from 'clsx';

import styles from '../../OverviewEventFeed.module.scss';
import { getCatalystEntityTag, parseCatalystData } from './utils';

export interface CatalystRowProps {
  event: EventFeedEvent;
  isLast?: boolean;
}

export const CatalystRow = ({ event, isLast }: CatalystRowProps) => {
  const { openDrawer: openCompanyCatalystDrawer } = useCatalystDrawer();
  const { openDrawer: openMarketCatalystDrawer } = useMarketCatalystDrawer();
  const { eventDate, isMarketWide, cardType, themeId, subCategory } =
    parseCatalystData(event);

  const entityTag = getCatalystEntityTag(event);

  const { data: entities } = useEntities({
    entityTags: entityTag ? [entityTag] : [],
    enabled: !!entityTag,
  });
  const entity = entities?.[0];

  const handleClick = () => {
    if (config.app.isTeamsApp) {
      return;
    }

    if (isMarketWide && subCategory) {
      openMarketCatalystDrawer({
        type: subCategory,
        themeId,
        insightId: event.source_id,
        source: POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW,
      });
    } else {
      openCompanyCatalystDrawer({
        type: 'company',
        entityTag: event.entity,
        insightId: event.source_id,
        source: POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW,
      });
    }
  };

  return (
    <div
      data-testid="catalyst-row"
      className={clsx(styles.feedItemWrapper, {
        [styles.last]: isLast,
        [styles.notClickable]: config.app.isTeamsApp,
      })}
    >
      <div className={styles.feedItem} onClick={handleClick}>
        <div className={styles.feedItemHeader}>
          <CardPill type={cardType} />
        </div>
        <p className={styles.insightTitle}>{event.title}</p>
        <div className={styles.statsRow}>
          <div className={styles.statsContainer}>
            {entity && (
              <InsightsLivePrice
                entity={entity}
                isHorizontalView
                showPricePercentage
                showDotSeparator
              />
            )}
          </div>
          <span className={styles.timeAgo}>{getCardDate(eventDate)}</span>
        </div>
      </div>
    </div>
  );
};
